import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './shadcn/button';
import { Card, CardContent } from './shadcn/card';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  MessageCircle,
  Building2,
  User,
  Send
} from 'lucide-react';

function ContactUs() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contact NeuralVision
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            We're here to help with any questions or support you may need
          </p>
        </motion.div>

        {/* Contact Information Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-16"
        >
          {/* Email Card */}
          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2">
            <CardContent className="p-8 text-center h-full flex flex-col justify-between">
              <div>
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Email Support</h3>
                <p className="text-gray-600 mb-4">Reach out to our support team directly</p>
              </div>
              <a
                href="mailto:info@neuralvision.ca"
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center justify-center"
              >
                info@neuralvision.ca
                <MessageCircle className="ml-2 h-4 w-4" />
              </a>
            </CardContent>
          </Card>

          {/* Phone Card */}
          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2">
            <CardContent className="p-8 text-center h-full flex flex-col justify-between">
              <div>
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Phone Support</h3>
                <p className="text-gray-600 mb-4">Call us during business hours</p>
              </div>
              <a
                href="tel:+1-604-968-2696"
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center justify-center"
              >
                +1 (604) 968-2696
                <Phone className="ml-2 h-4 w-4" />
              </a>
            </CardContent>
          </Card>

          {/* Address Card */}
          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2">
            <CardContent className="p-8 text-center">
              <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Our Location</h3>
              <p className="text-gray-600 mb-4">Visit us in person</p>
              <div className="text-gray-600">
                <div className="flex items-center justify-center mb-2">
                  <Building2 className="mr-2 h-4 w-4" />
                  NeuralVision Inc.
                </div>
                <div className="flex items-center justify-center mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  13495 Central Avenue
                </div>
                <div className="flex items-center justify-center">
                  <User className="mr-2 h-4 w-4" />
                  Surrey, BC V3T 0P1
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Business Hours Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <Card className="border-0 shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Business Hours</h2>
              <p className="text-gray-600">We're available to assist you during these times</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Clock className="h-5 w-5 text-blue-600 mr-3" />
                  <h3 className="font-semibold text-gray-900">Standard Support</h3>
                </div>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday:</span>
                    <span className="text-gray-900 font-medium">9:00 AM - 6:00 PM PST</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600">Saturday:</span>
                    <span className="text-gray-900 font-medium">10:00 AM - 4:00 PM PST</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600">Sunday:</span>
                    <span className="text-gray-900 font-medium">Closed</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Clock className="h-5 w-5 text-green-600 mr-3" />
                  <h3 className="font-semibold text-gray-900">Emergency Support</h3>
                </div>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span className="text-gray-600">24/7 Support:</span>
                    <span className="text-gray-900 font-medium">Available</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600">Response Time:</span>
                    <span className="text-gray-900 font-medium">Within 2 hours</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600">For Critical Issues:</span>
                    <span className="text-red-600 font-medium">Priority</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Contact Options */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <Card className="border-0 shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How Can We Help You?</h2>
              <p className="text-gray-600">Choose the best way to reach us for your specific needs</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Technical Support</h3>
                <p className="text-gray-600 mb-4">Issues with video processing, account access, or platform functionality</p>
                <a 
                  href="mailto:tech@neuralvision.ca" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  tech@neuralvision.ca
                  <Send className="ml-2 h-4 w-4" />
                </a>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Sales & Billing</h3>
                <p className="text-gray-600 mb-4">Questions about pricing, subscriptions, or billing issues</p>
                <a 
                  href="mailto:info@neuralvision.ca" 
                  className="inline-flex items-center text-green-600 hover:text-green-800 font-medium"
                >
                  info@neuralvision.ca
                  <Send className="ml-2 h-4 w-4" />
                </a>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">General Inquiries</h3>
                <p className="text-gray-600 mb-4">Partnerships, media, or general questions about our services</p>
                <a 
                  href="mailto:info@neuralvision.ca" 
                  className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                >
                  info@neuralvision.ca
                  <Send className="ml-2 h-4 w-4" />
                </a>
              </div>
              
              <div className="bg-yellow-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Account Management</h3>
                <p className="text-gray-600 mb-4">Account changes, data requests, or compliance questions</p>
                <a 
                  href="mailto:info@neuralvision.ca" 
                  className="inline-flex items-center text-yellow-600 hover:text-yellow-800 font-medium"
                >
                  info@neuralvision.ca
                  <Send className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-2xl mx-auto text-center mt-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Don't hesitate to reach out to us. Our team is always ready to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-3 text-lg">
                Contact Form
              </Button>
            </Link>
            <a href="tel:+1-604-968-2696">
              <Button variant="outline" className="border-2 px-8 py-3 text-lg">
                Call Now
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ContactUs;